import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { FaRegFile } from 'react-icons/fa';
import { IoClose, IoCloudUploadOutline, IoCheckmarkCircle } from 'react-icons/io5';

export type FileStatus = 'uploading' | 'success' | 'error';

const FILE_TYPE_EXTENSIONS: Record<string, string[]> = {
  image: ['jpg', 'png'],
  video: ['mov', 'mp4'],
};

const getAcceptedExtensions = (fileType?: string | string[]): string => {
  const types = Array.isArray(fileType) ? fileType : fileType ? [fileType] : [];
  return types.flatMap((t) => FILE_TYPE_EXTENSIONS[t] ?? []).join(', ');
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FILE_SIZE_LIMIT = 209715200; // 200 MB

interface FileUploadProps {
  fileType?: string | string[];
  multipleFiles?: boolean;
  onFiles: (files: File[]) => void;
  fileStatuses?: FileStatus[];
  disabled?: boolean;
}

const FileUpload = ({
  fileType,
  multipleFiles = false,
  onFiles,
  fileStatuses,
  disabled = false,
}: FileUploadProps) => {
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);
  const [sizeError, setSizeError] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const accept = (() => {
    const types = Array.isArray(fileType) ? fileType : fileType ? [fileType] : [];
    return types.map((t) => `${t}/*`).join(',');
  })();
  const acceptedExtensions = getAcceptedExtensions(fileType);

  const processFiles = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return;
    for (let i = 0; i < incoming.length; i++) {
      if (incoming[i].size >= FILE_SIZE_LIMIT) {
        setSizeError(true);
        return;
      }
    }
    setSizeError(false);
    const incomingArray = Array.from(incoming);
    const updated = multipleFiles ? [...selectedFiles, ...incomingArray] : incomingArray;
    setSelectedFiles(updated);
    onFiles(updated);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(e.target.files);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current++;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    processFiles(e.dataTransfer.files);
  };

  return (
    <div className="file-upload">
      {sizeError && (
        <div className="alert alert-danger">{intl.formatMessage({ id: 'fileSizeError' })}</div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multipleFiles}
        style={{ display: 'none' }}
        onChange={handleChange}
      />
      <div
        className={`file-upload-dropzone${isDragging ? ' dragging' : ''}${
          disabled ? ' disabled' : ''
        }`}
        onDragEnter={disabled ? undefined : handleDragEnter}
        onDragOver={disabled ? undefined : (e) => e.preventDefault()}
        onDragLeave={disabled ? undefined : handleDragLeave}
        onDrop={disabled ? undefined : handleDrop}
      >
        <p>
          <IoCloudUploadOutline className="mb-2" />
          {intl.formatMessage({ id: 'dragDropFiles' })}{' '}
          <button
            className="file-upload-browse-link"
            type="button"
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          >
            {intl.formatMessage({ id: 'browseFiles' })}
          </button>
        </p>
        {acceptedExtensions && <p className="file-upload-accepted">{acceptedExtensions}</p>}
      </div>
      {selectedFiles.length > 0 && (
        <ul className="file-upload-list">
          {selectedFiles.map((file, i) => {
            const status = fileStatuses?.[i];
            return (
              <li key={i}>
                {status === 'uploading' && <IoCloudUploadOutline />}
                {status === 'success' && (
                  <IoCheckmarkCircle className="file-upload-status success" />
                )}
                {(!status || status === 'error') && <FaRegFile />}
                <span>
                  <span>{file.name}</span>
                  <span className="file-details">
                    {formatFileSize(file.size)}
                    {status === 'uploading' && (
                      <span className="file-upload-status-text">
                        {intl.formatMessage({ id: 'uploading' })}...
                      </span>
                    )}
                    {status === 'error' && (
                      <span className="file-upload-status-text error">
                        {intl.formatMessage({ id: 'uploadFailed' })}
                      </span>
                    )}
                  </span>
                </span>
                {(!status || status === 'error') && (
                  <button
                    className="button transparent no-padding"
                    type="button"
                    onClick={() => {
                      const updated = selectedFiles.filter((_, j) => j !== i);
                      setSelectedFiles(updated);
                      onFiles(updated);
                    }}
                    style={{ marginLeft: 'auto', marginTop: '-2px' }}
                  >
                    <IoClose />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default FileUpload;
